import { useState, useEffect, useRef } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import Hls from 'hls.js';

const MAX_RETRIES = 3;

export const useHlsWithRetry = (streamId, retryDelay = 0) => {
  const [retransmission, setRetransmission] = useState(null);
  const [status, setStatus] = useState(null);
  const retryCountRef = useRef(0);

  const getRetranslation = async () => {
    setStatus('connecting');
    try {
      const data = await apiContainer.lunaClient.streams.getRetransmission(streamId);
      setRetransmission(data);
    } catch {
      retryCountRef.current += 1;
      if (retryCountRef.current <= MAX_RETRIES) {
        setTimeout(getRetranslation(), retryDelay);
        return;
      }
      setStatus('failed');
    }
  };

  const loadHls = () => {
    const { url, token } = retransmission;

    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          // eslint-disable-next-line no-param-reassign
          xhr.withCredentials = false;
        },
      });

      hls.loadSource(url);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        hls.destroy();
        setStatus('loaded');
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          retryCountRef.current += 1;

          if (retryCountRef.current <= MAX_RETRIES) {
            hls.destroy();
            setTimeout(getRetranslation(), retryDelay);
            return;
          }

          hls.destroy();
          setStatus('failed');
        }
      });
    }
  };

  const resetData = () => {
    setStatus(null);
    setRetransmission(null);
    retryCountRef.current = 0;
  };

  useEffect(() => {
    if (!streamId) {
      resetData();
      return;
    }
    if (retransmission) return;
    getRetranslation();
  }, [streamId]);

  useEffect(() => {
    if (!retransmission || status === 'failed') return;
    loadHls();
  }, [retransmission]);

  return { status, retransmission };
};
